import { RefObject, useEffect, useRef, useState } from 'react';

import { isBrowser } from '@lib/environment/isBrowser';

export type UseIntersectionObserverOptions = IntersectionObserverInit & {
  freezeOnceVisible?: boolean;
};

/**
 * Custom hook that uses the Intersection Observer API to track the visibility of a DOM element.
 * Will always return `true` if the browser does not support the Intersection Observer API.
 * @param options - The options for the Intersection Observer.
 * @param options.threshold - A number or an array of numbers between 0 and 1, representing the percentage of the target element's visibility needed for the callback to be invoked.
 * @param options.rootMargin - A string representing the margin around the root element.
 * @param options.freezeOnceVisible - A boolean indicating whether to freeze the visibility state once the element becomes visible.
 * @returns A tuple containing a boolean indicating whether the element is intersecting or has been rendered, and a ref object for the target element.
 */
export const useIntersectionObserver = ({
  threshold = 0,
  rootMargin = '0%',
  freezeOnceVisible = false,
}: UseIntersectionObserverOptions): [boolean, RefObject<HTMLDivElement | null>] => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasRendered, setHasRendered] = useState(false);
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hasIOSupport = isBrowser() && window.IntersectionObserver;

    if (!hasIOSupport) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry!.isIntersecting);
        if (entry!.isIntersecting && !hasRendered && !freezeOnceVisible) {
          if (count > 1) {
            setHasRendered(true);
          } else {
            setCount(prevCount => prevCount + 1);
          }
        }
      },
      { threshold, rootMargin },
    );

    const element = ref.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, hasRendered, count, rootMargin, freezeOnceVisible]);

  return [isIntersecting || hasRendered, ref];
};
